import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import { formatDateParam, parseDateParam } from "helpers";
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { CheckBox } from "ui/components/CheckBox";
import {
  getPricingCombinationsAPI,
  getPricingKeysAPI,
  getPricingPacksAPI,
  resolveManyPricingAPI,
} from "services/api/pricing/api";
import { selectMeteredKeys } from "services/api/pricing/consts";
import { getPricingSchemesAPI } from "services/api/pricing/schemeApi";
import { PricingScheme } from "services/api/pricing/schemeTypes";
import {
  PricingBreakdown,
  PricingCombinationOption,
  PricingKeyRow,
  PricingPack,
} from "services/api/pricing/types";
import {
  ANNUAL_DISCOUNT_KEY_CODE,
  CORE_PACK_CODE,
  MINIMUM_KEY_CODE,
  TRIAL_DAYS_KEY_CODE,
} from "services/api/subscriptions/consts";

import { estimateTenantSubscriptionAPI } from "services/api/subscriptions/api";
import {
  SubscriptionEstimate,
  SubscriptionOverrideFormValues,
} from "services/api/subscriptions/types";

import SubscriptionEstimateInputs from "./components/SubscriptionEstimateInputs/SubscriptionEstimateInputs";
import SubscriptionEstimatePanel from "./components/SubscriptionEstimatePanel/SubscriptionEstimatePanel";
import SubscriptionMeteredList from "./components/SubscriptionMeteredList/SubscriptionMeteredList";
import SubscriptionOverrideList from "./components/SubscriptionOverrideList/SubscriptionOverrideList";
import SubscriptionOverrideModal from "./components/SubscriptionOverrideModal/SubscriptionOverrideModal";
import BillingPeriodPicker from "./components/BillingPeriodPicker/BillingPeriodPicker";
import { BillingPeriod } from "./components/BillingPeriodPicker/types";
import SubscriptionPackPicker from "./components/SubscriptionPackPicker/SubscriptionPackPicker";
import SubscriptionPriceBreakdown from "./components/SubscriptionPriceBreakdown/SubscriptionPriceBreakdown";
import { OverridableKey, TenantSubscriptionStepProps } from "./types";

/** Ista pravila sortiranja koja backend koristi pri izvođenju ključa kombinacije. */
const sameCodes = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;

  const a = [...left].map((c) => c.toLowerCase()).sort();
  const b = [...right].map((c) => c.toLowerCase()).sort();

  return a.every((code, index) => code === b[index]);
};

const TenantSubscriptionStep: React.FC<TenantSubscriptionStepProps> = (props) => {
  const {
    tenantValues,
    formValues,
    setFormValues,
    existingSubscription,
    isEditing = false,
    showEstimate = true,
  } = props;

  const tenantId = tenantValues.id;
  const isPerson = tenantValues.tenantTypeId === "P";

  const [packs, setPacks] = useState<PricingPack[]>([]);
  const [schemes, setSchemes] = useState<PricingScheme[]>([]);
  const [combinations, setCombinations] = useState<PricingCombinationOption[]>([]);
  const [keys, setKeys] = useState<PricingKeyRow[]>([]);
  const [breakdowns, setBreakdowns] = useState<Record<string, PricingBreakdown>>({});
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [estimate, setEstimate] = useState<SubscriptionEstimate | null>(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [editedOverrideKey, setEditedOverrideKey] = useState<string | null>(null);

  /* Mjerene stavke se naplaćuju i klijentu bez pretplate, pa ih treba i grana za osobu. */
  const meteredKeys = useMemo(() => selectMeteredKeys(keys), [keys]);

  /*
    Ključ kombinacije se ne sastavlja u pregledniku — traži se u popisu koji je stigao s
    backenda. Tako se pravilo izvođenja ključa ne ponavlja na dva mjesta.
  */
  const combinationKeyCode = useMemo(() => {
    const match = combinations.find((c) => sameCodes(c.packCodes, formValues.packCodes));

    return match?.keyCode ?? null;
  }, [combinations, formValues.packCodes]);

  const fetchCatalog = useCallback(async () => {
    setIsLoadingCatalog(true);

    try {
      const [packsResult, schemesResult, combinationsResult, keysResult] = await Promise.all([
        getPricingPacksAPI(),
        getPricingSchemesAPI(),
        getPricingCombinationsAPI(formValues.startsOn),
        getPricingKeysAPI(),
      ]);

      setPacks(packsResult.filter((pack) => pack.isActive));
      setSchemes(schemesResult.filter((scheme) => scheme.isActive));
      setCombinations(combinationsResult);
      setKeys(keysResult);
    } catch {
      // poruka ide kroz snackbar
    }

    setIsLoadingCatalog(false);
  }, [formValues.startsOn]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  /*
    Sve vrijednosti dolaze iz resolvera, jednim pozivom. Frontend ne računa nijednu cijenu ni
    prag — samo prikazuje ono što je razriješeno.
  */
  const keyCodesToResolve = useMemo(() => {
    const codes = meteredKeys.map((key) => key.code);

    if (isPerson) {
      return codes;
    }

    return [
      ...(combinationKeyCode ? [combinationKeyCode] : []),
      MINIMUM_KEY_CODE,
      ANNUAL_DISCOUNT_KEY_CODE,
      TRIAL_DAYS_KEY_CODE,
      ...codes,
    ];
  }, [combinationKeyCode, isPerson, meteredKeys]);

  const fetchBreakdowns = useCallback(async () => {
    if (!tenantId || keyCodesToResolve.length === 0) {
      setBreakdowns({});
      return;
    }

    setIsResolving(true);

    try {
      const results = await resolveManyPricingAPI(
        tenantId,
        keyCodesToResolve,
        formValues.startsOn,
        formValues.pricingSchemeId
      );

      setBreakdowns(
        Object.fromEntries(results.map((breakdown) => [breakdown.keyCode, breakdown]))
      );
    } catch {
      // poruka ide kroz snackbar; radije prazno nego pogrešna cijena
      setBreakdowns({});
    }

    setIsResolving(false);
  }, [tenantId, keyCodesToResolve, formValues.startsOn, formValues.pricingSchemeId]);

  useEffect(() => {
    fetchBreakdowns();
  }, [fetchBreakdowns]);

  /*
    Živi izračun. Ni jedan iznos se ne računa ovdje — backend vraća gotove brojke, kroz isti
    resolver i ista pravila koja vrijede pri potpisu.
  */
  const fetchEstimate = useCallback(async () => {
    if (!tenantId || isPerson || combinationKeyCode === null || !showEstimate) {
      setEstimate(null);
      return;
    }

    setIsEstimating(true);

    try {
      setEstimate(await estimateTenantSubscriptionAPI(tenantId, formValues));
    } catch {
      // poruka ide kroz snackbar; radije prazno nego pogrešan iznos
      setEstimate(null);
    }

    setIsEstimating(false);
  }, [tenantId, isPerson, combinationKeyCode, formValues, showEstimate]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  const handleVehicleCountChange = useCallback(
    (value: number) => {
      setFormValues((prev) => ({ ...prev, estimatedVehicles: value }));
    },
    [setFormValues]
  );

  const handleQuantityChange = useCallback(
    (keyCode: string, value: number) => {
      setFormValues((prev) => ({
        ...prev,
        estimatedQuantities: { ...prev.estimatedQuantities, [keyCode]: value },
      }));
    },
    [setFormValues]
  );

  const meteredNames = useMemo(
    () => Object.fromEntries(meteredKeys.map((key) => [key.code, key.name])),
    [meteredKeys]
  );

  /*---------------------------------------------------------------
      PREGAZIVANJA

      Drže se u stanju čarobnjaka i šalju tek pri potpisu, u istoj
      transakciji s pretplatom.
    ---------------------------------------------------------------*/

  /*
    Nudi se samo kombinacija koju klijent uzima — na drugima nije, pa im pregazivanje ne bi
    imalo učinka. `nabava.*` nije ovdje jer nije pregaziv, što backend i odbija.
  */
  const overridableKeys: OverridableKey[] = useMemo(() => {
    const combination: OverridableKey[] = combinationKeyCode
      ? [{ keyCode: combinationKeyCode, name: "Cijena po vozilu" }]
      : [];

    return [
      ...combination,
      ...keys
        .filter((key) => key.isOverridable && key.isActive && key.code !== combinationKeyCode)
        .map((key) => ({ keyCode: key.code, name: key.name })),
    ];
  }, [combinationKeyCode, keys]);

  const overrideKeyNames = useMemo(
    () => Object.fromEntries(overridableKeys.map((key) => [key.keyCode, key.name])),
    [overridableKeys]
  );

  const handleOverrideAdd = useCallback(() => {
    setEditedOverrideKey(null);
    setIsOverrideModalOpen(true);
  }, []);

  const handleOverrideEdit = useCallback((keyCode: string) => {
    setEditedOverrideKey(keyCode);
    setIsOverrideModalOpen(true);
  }, []);

  const handleOverrideRemove = useCallback(
    (keyCode: string) => {
      setFormValues((prev) => ({
        ...prev,
        overrides: prev.overrides.filter((item) => item.keyCode !== keyCode),
      }));
    },
    [setFormValues]
  );

  const handleOverrideModalClose = useCallback(() => {
    setIsOverrideModalOpen(false);
    setEditedOverrideKey(null);
  }, []);

  /* Jedan unos po ključu — dva pregazivanja istog ključa backend ionako odbija kao preklapanje. */
  const handleOverrideSave = useCallback(
    (values: SubscriptionOverrideFormValues) => {
      setFormValues((prev) => ({
        ...prev,
        overrides: [
          ...prev.overrides.filter(
            (item) => item.keyCode !== values.keyCode && item.keyCode !== editedOverrideKey
          ),
          values,
        ],
      }));

      setIsOverrideModalOpen(false);
      setEditedOverrideKey(null);
    },
    [setFormValues, editedOverrideKey]
  );

  const editedOverride = useMemo(
    () => formValues.overrides.find((item) => item.keyCode === editedOverrideKey) ?? null,
    [formValues.overrides, editedOverrideKey]
  );

  const handlePackToggle = useCallback(
    (packCode: string, isSelected: boolean) => {
      if (packCode === CORE_PACK_CODE) return;

      setFormValues((prev) => ({
        ...prev,
        packCodes: isSelected
          ? [...prev.packCodes, packCode]
          : prev.packCodes.filter((code) => code !== packCode),
      }));
    },
    [setFormValues]
  );

  const handleSchemeChange = useCallback(
    (_event: any, _name: string, item: DropdownOption) => {
      setFormValues((prev) => ({
        ...prev,
        pricingSchemeId: item?.value ? String(item.value) : null,
      }));
    },
    [setFormValues]
  );

  const handleBillingPeriodChange = useCallback(
    (billingPeriod: BillingPeriod) => {
      setFormValues((prev) => ({ ...prev, billingPeriod }));
    },
    [setFormValues]
  );

  const handleStartsOnChange = useCallback(
    (_name: string, date: Date | null) => {
      const formatted = formatDateParam(date);

      if (!formatted) return;

      setFormValues((prev) => ({ ...prev, startsOn: formatted }));
    },
    [setFormValues]
  );

  const handleTrialChange = useCallback(
    (_event: any, isChecked: boolean) => {
      setFormValues((prev) => ({ ...prev, withTrial: isChecked }));
    },
    [setFormValues]
  );

  const schemeOptions: DropdownOption[] = useMemo(
    () => [
      { value: "", text: "Standardne cijene" },
      ...schemes.map((scheme) => ({ value: scheme.id, text: scheme.name })),
    ],
    [schemes]
  );

  const trialBreakdown = breakdowns[TRIAL_DAYS_KEY_CODE];
  const annualDiscountBreakdown = breakdowns[ANNUAL_DISCOUNT_KEY_CODE];

  /*---------------------------------------------------------------
      GRANA ZA OSOBU

      Fizička osoba ne plaća pretplatu. Mjereni troškovi se i dalje
      naplaćuju po komadu, pa ih treba vidjeti.
    ---------------------------------------------------------------*/
  if (isPerson) {
    return (
      <div className="flex items-start justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-3xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Pretplata</h1>
          <p className="text-center text-sm text-slate-600 mb-6">
            Fizička osoba nema pretplatu.
          </p>

          <div className="border border-emerald-200 bg-emerald-50 px-4 py-3 mb-6">
            <p className="text-sm text-emerald-900 font-medium">
              Korištenje je besplatno.
            </p>
            <p className="text-sm text-emerald-800 mt-1">
              Za tip <span className="font-medium">Osoba</span> ne ugovara se pretplata i ne
              naplaćuje se mjesečna naknada. Mjereni troškovi se naplaćuju po komadu.
            </p>
          </div>

          <SubscriptionMeteredList
            meteredKeys={meteredKeys}
            breakdowns={breakdowns}
            isLoading={isLoadingCatalog || isResolving}
          />
        </div>
      </div>
    );
  }

  /*---------------------------------------------------------------
      POSTOJEĆA PRETPLATA

      Klijent ne može imati dvije, pa čarobnjak za unos ovdje staje.
      Ekran klijenta prosljeđuje `isEditing` i time otvara isti
      obrazac za izmjenu.
    ---------------------------------------------------------------*/
  if (existingSubscription && !isEditing) {
    return (
      <div className="flex items-start justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-3xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Pretplata</h1>
          <p className="text-center text-sm text-slate-600 mb-6">
            Klijent je već ima.
          </p>

          <div className="border border-slate-200 px-4 py-3">
            <p className="text-sm text-slate-700">
              Ugovorena je kombinacija{" "}
              <span className="font-medium">{existingSubscription.packCombinationKey}</span>, od{" "}
              {new Date(existingSubscription.startsOn).toLocaleDateString("hr-HR")}.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Nova se ne ugovara ovdje. Izmjena ide preko ekrana klijenta.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /*---------------------------------------------------------------
      GRANA ZA TVRTKU
    ---------------------------------------------------------------*/
  return (
    <div className="flex items-start justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Pretplata</h1>
        <p className="text-center text-sm text-slate-600 mb-6">
          Odaberite pakete i način naplate. Sve cijene dolaze iz cjenika.
        </p>

        <div className="mb-6">
          <SubscriptionPackPicker
            packs={packs}
            selectedCodes={formValues.packCodes}
            onToggle={handlePackToggle}
            hasUnknownCombination={!isLoadingCatalog && combinationKeyCode === null}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <Dropdown
              name="pricingSchemeId"
              label="Cjenovna grupa"
              width="full"
              placeholder="Standardne cijene"
              value={formValues.pricingSchemeId ?? ""}
              options={schemeOptions}
              onChange={handleSchemeChange}
            />
          </div>

          {/*
            Kod izmjene se početak ne nudi: on je odluka s potpisa i backend ga ne mijenja.
            Umjesto polja stoji podatak od kada pretplata teče.
          */}
          <div>
            {isEditing ? (
              <>
                <p className="text-sm font-medium text-slate-700 mb-1">Početak pretplate</p>

                <p className="text-sm text-slate-500 py-2.5">
                  {existingSubscription
                    ? new Date(existingSubscription.startsOn).toLocaleDateString("hr-HR")
                    : "—"}
                </p>
              </>
            ) : (
              <DatePicker
                name="startsOn"
                label="Početak pretplate"
                width="full"
                value={parseDateParam(formValues.startsOn)}
                onChange={handleStartsOnChange}
              />
            )}
          </div>
        </div>

        <div className="mb-6">
          <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">
            Način plaćanja
          </p>

          {/* Popust sada stoji na kartici koja ga nosi, pa nema zasebne rečenice ispod. */}
          <BillingPeriodPicker
            value={formValues.billingPeriod === "Annual" ? "Annual" : "Monthly"}
            annualDiscountPercent={annualDiscountBreakdown?.effective ?? null}
            onChange={handleBillingPeriodChange}
          />
        </div>

        {/* Probni rok se ne uključuje naknadno; kod izmjene se samo vidi je li ga bilo. */}
        {isEditing ? (
          existingSubscription?.trialEndsOn && (
            <div className="mb-6">
              <p className="text-xs text-slate-500">
                Probni rok teče do{" "}
                {new Date(existingSubscription.trialEndsOn).toLocaleDateString("hr-HR")}.
              </p>
            </div>
          )
        ) : (
          <div className="mb-6">
            <CheckBox
              name="withTrial"
              label="Probni period"
              checked={formValues.withTrial}
              onChange={handleTrialChange}
            />

            {trialBreakdown && (
              <p className="text-xs text-slate-500 mt-1">
                Traje {trialBreakdown.effective} dana od početka pretplate.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-6">
          <SubscriptionPriceBreakdown
            label="Cijena po vozilu"
            breakdown={combinationKeyCode ? breakdowns[combinationKeyCode] ?? null : null}
            unitNote="Mjesečno, prije cjenovnih razreda po broju vozila."
            isLoading={isLoadingCatalog || isResolving}
          />

          <SubscriptionPriceBreakdown
            label="Minimalna naknada"
            breakdown={breakdowns[MINIMUM_KEY_CODE] ?? null}
            unitNote="Mjesečno, naplaćuje se ako je obračun po vozilima manji."
            isLoading={isLoadingCatalog || isResolving}
          />
        </div>

        <div className="mb-6">
          <SubscriptionMeteredList
            meteredKeys={meteredKeys}
            breakdowns={breakdowns}
            isLoading={isLoadingCatalog || isResolving}
          />
        </div>

        <div className="mb-6">
          <SubscriptionOverrideList
            overrides={formValues.overrides}
            keyNames={overrideKeyNames}
            onAdd={handleOverrideAdd}
            onEdit={handleOverrideEdit}
            onRemove={handleOverrideRemove}
          />
        </div>

        {/* Ondje gdje je korak samo prikaz kalkulacija se seli u zaseban modal, da ne stoji mrtva. */}
        {showEstimate && (
          <>
            <div className="mb-6">
              <SubscriptionEstimateInputs
                vehicleCount={formValues.estimatedVehicles}
                meteredKeys={meteredKeys}
                quantities={formValues.estimatedQuantities}
                onVehicleCountChange={handleVehicleCountChange}
                onQuantityChange={handleQuantityChange}
              />
            </div>

            <SubscriptionEstimatePanel
              estimate={estimate}
              meteredNames={meteredNames}
              isLoading={isEstimating}
            />
          </>
        )}
      </div>

      <SubscriptionOverrideModal
        show={isOverrideModalOpen}
        tenantId={tenantId}
        startsOn={formValues.startsOn}
        pricingSchemeId={formValues.pricingSchemeId}
        overridableKeys={overridableKeys}
        existing={editedOverride}
        onSave={handleOverrideSave}
        onClose={handleOverrideModalClose}
      />
    </div>
  );
};

export default memo(TenantSubscriptionStep);
